import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * FilterBuilder Component
 * 
 * Constructor visual de filtros complejos con soporte para:
 * - Condiciones individuales (field, operator, value)
 * - Grupos anidados con lógica AND/OR
 * - Validación en tiempo real
 * - Selección dinámica de operadores según tipo de campo
 */

const FilterBuilder = ({ value, onChange, onValidate }) => {
  const [columnas, setColumnas] = useState({});
  const [loading, setLoading] = useState(true);
  const [validationError, setValidationError] = useState(null);

  // Cargar columnas filtrables al montar el componente
  useEffect(() => {
    const fetchColumnas = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/contratos/columnas-filtrables', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setColumnas(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error cargando columnas:', error);
        setLoading(false);
      }
    };
    fetchColumnas();
  }, []);

  // Validar filtros en tiempo real
  useEffect(() => {
    if (!value || Object.keys(columnas).length === 0) return;

    const validateFilters = async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.post('/api/contratos/validar-filtros', 
          { filtros_json: value },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setValidationError(null);
        if (onValidate) onValidate(true, null);
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Error validando filtros';
        setValidationError(errorMsg);
        if (onValidate) onValidate(false, errorMsg);
      }
    };

    // Debounce validation
    const timer = setTimeout(validateFilters, 500);
    return () => clearTimeout(timer);
  }, [value, columnas, onValidate]);

  // Agrupar columnas por categoría
  const columnasPorCategoria = {
    'Cliente': ['nombre_completo', 'tipo_persona', 'identificador', 'cups', 'provincia', 'municipio', 'cnae'],
    'Contrato': ['canal', 'codigo', 'fecha_inicio', 'fecha_fin', 'fecha_inicio_producto', 'fecha_fin_producto', 
                 'id_tarifa_precios', 'tipo_tarifa_precio', 'tarifa_atr', 'estado', 'subestado', 'permanencia'],
    'Consumo y Potencias': ['consumo_anual_estimado', 'potencia_contratada_p1_kw', 'potencia_contratada_p2_kw', 
                            'potencia_contratada_p3_kw', 'potencia_contratada_p4_kw', 'potencia_contratada_p5_kw', 
                            'potencia_contratada_p6_kw'],
    'Precios': ['e1_base', 'e2_base', 'e3_base', 'e4_base', 'e5_base', 'e6_base', 
                'p1_base', 'p2_base', 'p3_base', 'p4_base', 'p5_base', 'p6_base', 'fee_indexado'],
    'Estado y Liquidación': ['liquidacion_estado', 'decomision_estado', 'estado_historificacion', 'liquidacion_tipo'],
    'Otros': [] // Columnas que no están categorizadas
  };

  // Agrupar columnas restantes en "Otros"
  const columnasCategorizadas = new Set(Object.values(columnasPorCategoria).flat());
  columnasPorCategoria['Otros'] = Object.keys(columnas).filter(col => !columnasCategorizadas.has(col));

  const operadoresPorTipo = {
    text: [
      { value: 'equals', label: 'Es igual a' },
      { value: 'not_equals', label: 'No es igual a' },
      { value: 'contains', label: 'Contiene' },
      { value: 'starts_with', label: 'Empieza con' },
      { value: 'ends_with', label: 'Termina con' },
      { value: 'in', label: 'Está en lista' },
      { value: 'not_in', label: 'No está en lista' }
    ],
    integer: [
      { value: 'equals', label: '=' },
      { value: 'not_equals', label: '≠' },
      { value: 'gt', label: '>' },
      { value: 'lt', label: '<' },
      { value: 'gte', label: '≥' },
      { value: 'lte', label: '≤' },
      { value: 'between', label: 'Entre' },
      { value: 'in', label: 'Está en lista' },
      { value: 'not_in', label: 'No está en lista' }
    ],
    numeric: [
      { value: 'equals', label: '=' },
      { value: 'not_equals', label: '≠' },
      { value: 'gt', label: '>' },
      { value: 'lt', label: '<' },
      { value: 'gte', label: '≥' },
      { value: 'lte', label: '≤' },
      { value: 'between', label: 'Entre' },
      { value: 'in', label: 'Está en lista' },
      { value: 'not_in', label: 'No está en lista' }
    ],
    date: [
      { value: 'equals', label: '=' },
      { value: 'not_equals', label: '≠' },
      { value: 'gt', label: 'Después de' },
      { value: 'lt', label: 'Antes de' },
      { value: 'gte', label: 'Desde' },
      { value: 'lte', label: 'Hasta' },
      { value: 'between', label: 'Entre' }
    ],
    boolean: [
      { value: 'equals', label: 'Es' },
      { value: 'not_equals', label: 'No es' }
    ]
  };

  const updateFilter = (newValue) => {
    onChange(newValue);
  };

  const addCondition = (path = []) => {
    const newFilter = JSON.parse(JSON.stringify(value || { logic: 'AND', rules: [] }));
    const target = getTargetByPath(newFilter, path);
    
    target.rules.push({
      type: 'condition',
      field: '',
      operator: '',
      value: ''
    });
    
    updateFilter(newFilter);
  };

  const addGroup = (path = []) => {
    const newFilter = JSON.parse(JSON.stringify(value || { logic: 'AND', rules: [] }));
    const target = getTargetByPath(newFilter, path);
    
    target.rules.push({
      type: 'group',
      logic: 'AND',
      rules: []
    });
    
    updateFilter(newFilter);
  };

  const removeRule = (path) => {
    const newFilter = JSON.parse(JSON.stringify(value));
    const parentPath = path.slice(0, -1);
    const index = path[path.length - 1];
    const parent = getTargetByPath(newFilter, parentPath);
    
    parent.rules.splice(index, 1);
    updateFilter(newFilter);
  };

  const updateRule = (path, updates) => {
    const newFilter = JSON.parse(JSON.stringify(value));
    const rule = getTargetByPath(newFilter, path);
    
    Object.assign(rule, updates);
    updateFilter(newFilter);
  };

  const getTargetByPath = (filter, path) => {
    let target = filter;
    for (const index of path) {
      target = target.rules[index];
    }
    return target;
  };

  const toggleLogic = (path) => {
    const newFilter = JSON.parse(JSON.stringify(value));
    const target = getTargetByPath(newFilter, path);
    target.logic = target.logic === 'AND' ? 'OR' : 'AND';
    updateFilter(newFilter);
  };

  if (loading) {
    return <div className="text-gray-500">Cargando columnas filtrables...</div>;
  }

  if (!value) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        <p className="text-gray-500 mb-4">No hay filtros definidos</p>
        <button
          onClick={() => updateFilter({ logic: 'AND', rules: [] })}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Iniciar filtros
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Validación */}
      {validationError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <strong>Error de validación:</strong> {validationError}
        </div>
      )}
      
      {/* Filtro principal */}
      <RuleGroup
        group={value}
        path={[]}
        columnas={columnas}
        columnasPorCategoria={columnasPorCategoria}
        operadoresPorTipo={operadoresPorTipo}
        onAddCondition={addCondition}
        onAddGroup={addGroup}
        onRemoveRule={removeRule}
        onUpdateRule={updateRule}
        onToggleLogic={toggleLogic}
        isRoot={true}
      />
    </div>
  );
};

const RuleGroup = ({ 
  group, 
  path, 
  columnas, 
  columnasPorCategoria,
  operadoresPorTipo, 
  onAddCondition, 
  onAddGroup, 
  onRemoveRule, 
  onUpdateRule, 
  onToggleLogic,
  isRoot 
}) => {
  return (
    <div className={`border-l-4 ${group.logic === 'AND' ? 'border-blue-500' : 'border-green-500'} pl-4 py-2`}>
      <div className="flex items-center gap-2 mb-3">
        {/* Toggle AND/OR */}
        <button
          onClick={() => onToggleLogic(path)}
          className={`px-3 py-1 rounded font-semibold text-sm ${
            group.logic === 'AND' 
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
              : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          {group.logic}
        </button>
        
        {/* Botones de acción */}
        <button
          onClick={() => onAddCondition(path)}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
          title="Añadir condición"
        >
          + Condición
        </button>
        
        <button
          onClick={() => onAddGroup(path)}
          className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
          title="Añadir grupo"
        >
          + Grupo
        </button>
        
        {!isRoot && (
          <button
            onClick={() => onRemoveRule(path)}
            className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1 rounded ml-auto"
            title="Eliminar grupo"
          >
            × Eliminar grupo
          </button>
        )}
      </div>
      
      {/* Reglas */}
      <div className="space-y-2">
        {group.rules.map((rule, index) => {
          const rulePath = [...path, index];
          
          if (rule.type === 'condition') {
            return (
              <Condition
                key={index}
                condition={rule}
                path={rulePath}
                columnas={columnas}
                columnasPorCategoria={columnasPorCategoria}
                operadoresPorTipo={operadoresPorTipo}
                onRemoveRule={onRemoveRule}
                onUpdateRule={onUpdateRule}
              />
            );
          } else {
            return (
              <RuleGroup
                key={index}
                group={rule}
                path={rulePath}
                columnas={columnas}
                columnasPorCategoria={columnasPorCategoria}
                operadoresPorTipo={operadoresPorTipo}
                onAddCondition={onAddCondition}
                onAddGroup={onAddGroup}
                onRemoveRule={onRemoveRule}
                onUpdateRule={onUpdateRule}
                onToggleLogic={onToggleLogic}
                isRoot={false}
              />
            );
          }
        })}
        
        {group.rules.length === 0 && (
          <div className="text-gray-400 text-sm italic">Sin reglas. Añade una condición o grupo.</div>
        )}
      </div>
    </div>
  );
};

const Condition = ({ 
  condition, 
  path, 
  columnas, 
  columnasPorCategoria,
  operadoresPorTipo, 
  onRemoveRule, 
  onUpdateRule 
}) => {
  const fieldType = columnas[condition.field];
  const operators = fieldType ? operadoresPorTipo[fieldType] : [];
  
  const handleFieldChange = (field) => {
    onUpdateRule(path, { field, operator: '', value: '' });
  };
  
  const handleOperatorChange = (operator) => {
    onUpdateRule(path, { operator, value: '' });
  };
  
  const handleValueChange = (value) => {
    onUpdateRule(path, { value });
  };
  
  const renderValueInput = () => {
    if (!condition.field || !condition.operator) {
      return <input type="text" disabled className="flex-1 px-3 py-2 border rounded bg-gray-50" placeholder="Selecciona campo y operador primero" />;
    }
    
    const fieldType = columnas[condition.field];
    
    // Operadores que requieren lista de valores
    if (['in', 'not_in'].includes(condition.operator)) {
      const valueArray = Array.isArray(condition.value) ? condition.value : [];
      const valueStr = valueArray.join(', ');
      
      return (
        <input
          type="text"
          value={valueStr}
          onChange={(e) => {
            const values = e.target.value.split(',').map(v => v.trim()).filter(v => v);
            handleValueChange(values);
          }}
          className="flex-1 px-3 py-2 border rounded"
          placeholder="Valores separados por coma"
        />
      );
    }
    
    // Operador between requiere 2 valores
    if (condition.operator === 'between') {
      const valueArray = Array.isArray(condition.value) ? condition.value : ['', ''];
      
      return (
        <div className="flex-1 flex gap-2">
          <input
            type={fieldType === 'date' ? 'date' : 'text'}
            value={valueArray[0] || ''}
            onChange={(e) => handleValueChange([e.target.value, valueArray[1] || ''])}
            className="flex-1 px-3 py-2 border rounded"
            placeholder="Mínimo"
          />
          <span className="self-center">y</span>
          <input
            type={fieldType === 'date' ? 'date' : 'text'}
            value={valueArray[1] || ''}
            onChange={(e) => handleValueChange([valueArray[0] || '', e.target.value])}
            className="flex-1 px-3 py-2 border rounded"
            placeholder="Máximo"
          />
        </div>
      );
    }
    
    // Campos booleanos
    if (fieldType === 'boolean') {
      return (
        <select
          value={condition.value}
          onChange={(e) => handleValueChange(e.target.value === 'true')}
          className="flex-1 px-3 py-2 border rounded"
        >
          <option value="">Selecciona...</option>
          <option value="true">Verdadero</option>
          <option value="false">Falso</option>
        </select>
      );
    }
    
    // Campos de fecha
    if (fieldType === 'date') {
      return (
        <input
          type="date"
          value={condition.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className="flex-1 px-3 py-2 border rounded"
        />
      );
    }
    
    // Campos numéricos
    if (fieldType === 'integer' || fieldType === 'numeric') {
      return (
        <input
          type="number"
          step={fieldType === 'numeric' ? '0.01' : '1'}
          value={condition.value || ''}
          onChange={(e) => handleValueChange(e.target.value)}
          className="flex-1 px-3 py-2 border rounded"
          placeholder="Valor numérico"
        />
      );
    }
    
    // Campos de texto por defecto
    return (
      <input
        type="text"
        value={condition.value || ''}
        onChange={(e) => handleValueChange(e.target.value)}
        className="flex-1 px-3 py-2 border rounded"
        placeholder="Valor"
      />
    );
  };
  
  return (
    <div className="flex items-center gap-2 bg-gray-50 p-3 rounded">
      {/* Selector de campo */}
      <select
        value={condition.field}
        onChange={(e) => handleFieldChange(e.target.value)}
        className="px-3 py-2 border rounded bg-white"
      >
        <option value="">Selecciona campo...</option>
        {Object.entries(columnasPorCategoria).map(([categoria, cols]) => (
          cols.length > 0 && (
            <optgroup key={categoria} label={categoria}>
              {cols.map(col => (
                <option key={col} value={col}>
                  {col} ({columnas[col]})
                </option>
              ))}
            </optgroup>
          )
        ))}
      </select>
      
      {/* Selector de operador */}
      <select
        value={condition.operator}
        onChange={(e) => handleOperatorChange(e.target.value)}
        className="px-3 py-2 border rounded bg-white"
        disabled={!condition.field}
      >
        <option value="">Operador...</option>
        {operators.map(op => (
          <option key={op.value} value={op.value}>
            {op.label}
          </option>
        ))}
      </select>
      
      {/* Input de valor */}
      {renderValueInput()}
      
      {/* Botón eliminar */}
      <button
        onClick={() => onRemoveRule(path)}
        className="text-red-600 hover:text-red-800 px-2"
        title="Eliminar condición"
      >
        ×
      </button>
    </div>
  );
};

export default FilterBuilder;
